import type { ReactiveMap } from "@solid-primitives/map"
import { CatmullRomCurve3, Group, Vector3 } from "three"
import { NeighborWaterCurrentCollection } from "../../../../rx/memo/useNodeNeighborBaseWaterCurrentReactiveMap"
import type { WaterFlowNode } from "../../../../rx/memo/useSharedWaterFlowNodeMapMemo"
import eightDirectionCharsLowerCase from "../../../core/direction/eightDirectionCharsLowerCase"
import type { EntityWaterWithCurrent } from "../../../core/entity/isEntityWaterWithCurrent"
import { HALF_VERTICES_PER_TILE, VERTICES_PER_TILE } from "../../consts"
import createDebugTextSprite from "../../fn/createDebugTextSprite"
import swapVector3YZThenNegativeY from "../../fn/swapVector3YZThenNegativeY"
import createWaterCurrentParticleLinePoints from "../WaterCurrent/createWaterCurrentParticleLinePoints"
import setTileRelativeWaterCurrentVector3 from "../WaterCurrent/setTileRelativeWaterCurrentVector3"

const SHOW_DEBUG_MARKERS = false

const current2 = new Vector3()
const divisions = 9
const step = VERTICES_PER_TILE / divisions
const halfStep = step / 2

export default function appendTerrainMesh1WaterFlowParticleCurvesV2(
  entsWithCurrentList: Array<EntityWaterWithCurrent>,
  waterFlowNodeMap: Map<number, WaterFlowNode>,
  nodeNeighborBaseWaterCurrentReactiveMap: ReactiveMap<number, NeighborWaterCurrentCollection>,
  particleCurves: Array<CatmullRomCurve3>,
  particleBoundingBoxPoints: Array<Vector3>,
  group: Group,
  gridWidth: number,
): void {
  // console.log('<appendTerrainMesh1WaterFlowParticleCurvesV2>', entsWithCurrentList.length, particleCurves.length)
  // console.time('appendTerrainMesh1WaterFlowParticleCurvesV2')
  let ent: EntityWaterWithCurrent
  for (let i = 0; i < entsWithCurrentList.length; i++) {
    ent = entsWithCurrentList[i]
    const { current } = ent
    const nodeIndex = ent.x + ent.y * gridWidth
    const eightDirectionCurrentIndex = current.edc
    const dirChar = eightDirectionCharsLowerCase[eightDirectionCurrentIndex]
    const wx = ent.x * VERTICES_PER_TILE
    const wy = ent.y * VERTICES_PER_TILE
    const wz = 4
    if (SHOW_DEBUG_MARKERS) {
      const dt = createDebugTextSprite(`EDC: ${(eightDirectionCurrentIndex ?? 'null')}\nIndex: ${nodeIndex}\nID: ${ent.id}\n(${ent.x}, ${ent.y})\n${dirChar}`)
      // dt.visible = true
      dt.position.set(wx, wz, wy)
      dt.scale.multiplyScalar(2)
      group.add(dt)
    }

    const minX = wx - HALF_VERTICES_PER_TILE + halfStep
    const minY = wy - HALF_VERTICES_PER_TILE + halfStep
    const maxX = wx + HALF_VERTICES_PER_TILE - halfStep
    const maxY = wy + HALF_VERTICES_PER_TILE - halfStep

    for (let wx1 = minX; wx1 <= maxX; wx1 += step) {
      for (let wy1 = minY; wy1 <= maxY; wy1 += step) {

        // if (ent.x < 1 || ent.x > 4 || ent.y < 1 || ent.y > 4) {
        //   continue
        // }

        const wz = 1

        const origin1 = new Vector3(wx1, wz, wy1)

        const nwcCollection = nodeNeighborBaseWaterCurrentReactiveMap.get(nodeIndex)
        if (nwcCollection) {
          // const dirV3 = getEntityCurrentVector3(current)
          // convert wx1, wy1 to rx, ry
          const tx = (wx1 - wx)
          const ty = (wy1 - wy)

          setTileRelativeWaterCurrentVector3(nwcCollection, tx, ty, current2)

          // if (!(current2.length() > 0.1)) {
          //   console.warn('current2.length() > 0.1')
          // }
          if (current2.length() > 0.1) {
            const linePoints = createWaterCurrentParticleLinePoints(nodeNeighborBaseWaterCurrentReactiveMap, waterFlowNodeMap, gridWidth, origin1, current2)
            // if (linePoints.length !== 3) {
            //   console.warn('linePoints.length !== 3', linePoints.length)
            // }
            if (linePoints) {
              swapVector3YZThenNegativeY(linePoints)
              const curve = new CatmullRomCurve3(linePoints)
              particleCurves.push(curve)
              particleBoundingBoxPoints.push(...linePoints)
            }
          }
        }
      }
    }
  }
  // console.timeEnd('appendTerrainMesh1WaterFlowParticleCurvesV2')
  // console.log('</appendTerrainMesh1WaterFlowParticleCurvesV2>', particleCurves.length)
}
